import * as React from 'react';
// @ts-ignore
import { useEffect } from 'react';

import Field from '../components/Field';
import ErrorAlert from '../components/ErrorAlert';
import { File } from './components/File/File';
import { TextArea } from './components/TextArea/TextArea';
import { CheckBox } from './components/CheckBox/CheckBox';
import { Alert } from './components/Alert/Alert';

import { useToggle } from '../../../hooks/useToggle';
import {
  checkValidFormat,
  createEmptyInput,
  createValueToSend,
  getDefaultInputs,
  isExistFileValues,
} from './helpers';

import type {
  CreateFiles,
  FilesProps,
  FilesSchema,
  FileValue,
} from './Files.props';

import { DEFAULT_MAX_SIZE, DEFAULT_FILE_SIZE } from './Files.constants';

const FilesControl = ({ api, schema, dispatcher, error }: FilesProps) => {
  // @ts-ignore
  const [inputs, setInputs] = React.useState<Array<FileValue>>(
    getDefaultInputs(schema?.value?.inputs),
  );
  // @ts-ignore
  const [comment, setComment] = React.useState<string | null>(
    schema?.value?.comment || '',
  );
  // @ts-ignore
  const [warning, setWarning] = React.useState<string | null>(null);
  // @ts-ignore
  const [isLoadingFile, setIsLoadingFile] = React.useState<
    Record<number, boolean>
  >({});

  const [isEnabledComment, _, setEnabledComment] = useToggle(
    !!schema?.value?.isComment,
  );
  const [isNotification, toggleNotification] = useToggle(false);
  const [isDescription, toggleDescription] = useToggle(false);

  const handleLoadingInput = (id: string, isLoading: boolean) => {
    setIsLoadingFile((prevLoadings) => ({
      ...prevLoadings,
      [id]: isLoading,
    }));
  };

  const onUpload =
    (idx: number, fileId: string) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      const maxSize = schema?.extra?.maxSize ?? DEFAULT_MAX_SIZE;
      setWarning(null);

      if (files?.[0]?.size > maxSize) {
        setWarning(
          `Размер файла превышает допустимый размер в ${Math.trunc(
            maxSize / DEFAULT_FILE_SIZE,
          )} Мб`,
        );
        event.target.value = '';
        return;
      }

      if (
        Array.isArray(schema?.format) &&
        !checkValidFormat(files?.[0], schema?.format)
      ) {
        setWarning(`Неверный формат файла: ${files?.[0]?.type}`);
        event.target.value = '';
        return;
      }

      handleLoadingInput(fileId, true);

      const { moduleApi, filesUrl, fileUrlEndpoint } = api;

      // если вдруг в каком-то сервисе нужно добавить или убрать слеш
      // или просто поменять эндпоинт - прокидываем это из Utils сервиса
      const fileUrlEndpointWithServiceSpecificity = fileUrlEndpoint || '/file/';
      const apiUrl = `${filesUrl}${
        schema?.field ?? schema?.id
      }${fileUrlEndpointWithServiceSpecificity}`;

      moduleApi &&
        moduleApi?.uploadFile(
          {
            apiUrl,
            fileId,
            file: files[0],
          },
          {
            success: ({ data }: { data: FileValue }) => {
              const updatedInputs = inputs.map((input) => {
                if (input.id !== data.id) {
                  return input;
                }
                return data;
              });
              setInputs(updatedInputs);
              handleLoadingInput(fileId, false);
              dispatcher.onBlur(
                schema?.field ?? schema.id,
                createValueToSend({ inputs: updatedInputs }),
                true,
                undefined,
              );
            },
            error: (error) => {},
          },
        );
    };

  const onAddEmptyInput = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setInputs((prevInputs) => [createEmptyInput(), ...prevInputs]);
  };

  const onDelete =
    (idx: number, fieldId: string | null) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const input = inputs?.[idx];

      if (!input?.name && !input?.url) {
        setInputs((prevInputs) => prevInputs.filter((f) => f.id !== fieldId));
        return;
      }
      const { moduleApi, filesUrl } = api;

      moduleApi &&
        moduleApi?.deleteFile(
          { apiUrl: `${filesUrl}${schema?.field ?? schema.id}/${fieldId}` },
          {
            success: () => {
              const withoutRemoved = inputs?.filter(
                (input) => input?.id !== fieldId,
              );
              const inputsUpdated = withoutRemoved.length
                ? { inputs: withoutRemoved }
                : null;

              setInputs(
                !withoutRemoved?.length ? [createEmptyInput()] : withoutRemoved,
              );
              dispatcher.onBlur(
                schema?.field ?? schema.id,
                inputsUpdated,
                true,
                undefined,
              );
            },
            error: () => {},
          },
        );
    };

  const handleToggleComment = () => {
    setEnabledComment((isComment) => {
      const _value = createValueToSend({
        isComment: !isComment,
        comment,
        inputs,
      });
      dispatcher.onBlur(schema?.field ?? schema.id, _value, true, undefined);

      return !isComment;
    });
  };

  // @ts-ignore
  const handleComment = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComment(event.target.value);
    },
    [],
  );

  const handleBlurComment = (
    event?: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const _value = createValueToSend({
      isComment: isEnabledComment,
      comment,
      inputs,
    });
    dispatcher.onBlur(schema?.field ?? schema.id, _value, true, undefined);
  };

  // @ts-ignore
  useEffect(() => {
    if (schema?.field && schema?.value?.inputs) {
      setInputs(schema.value.inputs);
    }
  }, [schema.value]);

  return (
    <>
      <Alert classNames="hint" state text={schema?.extra?.hint} />

      {!isEnabledComment &&
        inputs?.map((file, idx) => (
          <div className="input-group" key={file?.id ?? idx}>
            <File
              isBase64={!!file?.isBase64}
              isLoading={isLoadingFile?.[file.id]}
              onChange={onUpload(idx, file.id)}
              value={file}
              disabled={schema.disabled}
            />
            {/* TODO: Убрать кнопки в отдельный компонент */}
            <div className="input-group-btn">
              {!schema?.disabled && (
                <>
                  {schema?.notification && (
                    <button
                      type="button"
                      onClick={toggleNotification}
                      className="btn btn-default btn-add"
                    >
                      <span className="glyphicon glyphicon-info-sign" />
                    </button>
                  )}
                  {schema?.description && (
                    <button
                      type="button"
                      onClick={toggleDescription}
                      className="btn btn-default btn-add"
                    >
                      <span className="glyphicon glyphicon-question-sign" />
                    </button>
                  )}
                </>
              )}
              {inputs?.length < schema?.maxFiles &&
                idx === inputs?.length - 1 && (
                  <button
                    title="Добавить еще"
                    type="button"
                    className="btn btn-default btn-add"
                    disabled={schema?.disabled}
                    onClick={onAddEmptyInput}
                  >
                    <span className="glyphicon glyphicon-plus-sign" />
                  </button>
                )}
              {(file?.url ||
                (inputs?.length && idx !== inputs?.length - 1)) && (
                <button
                  disabled={
                    schema?.activeDeleteBtn
                      ? !schema?.activeDeleteBtn
                      : schema?.disabled
                  }
                  type="button"
                  className="btn btn-danger btn-remove"
                  onClick={onDelete(idx, file?.id)}
                >
                  Удалить
                </button>
              )}
            </div>
          </div>
        ))}
      {schema?.isEnableComment && !isExistFileValues(inputs) && (
        <div>
          <CheckBox
            label="Нет файлов?"
            id={schema.id}
            checked={isEnabledComment}
            onChange={handleToggleComment}
          />
          {isEnabledComment && (
            <TextArea
              value={comment}
              onChange={handleComment}
              onBlur={handleBlurComment}
            />
          )}
        </div>
      )}

      {error && error?.message && <ErrorAlert errorMessage={error.message} />}

      <Alert
        type="warning"
        state={isNotification}
        text={schema?.notification?.ru}
      />
      <Alert
        type="warning"
        state={isDescription}
        text={schema?.description?.ru}
      >
        {!!schema?.format?.length && (
          <div>
            Допустимые форматы файлов для загрузки: {schema.format.join(',')}.
            <br />
            Максимальный размер файла не должен превышать{' '}
            {Math.trunc(
              (schema?.extra?.maxSize || DEFAULT_MAX_SIZE) / DEFAULT_FILE_SIZE,
            )}
            Мб
          </div>
        )}
      </Alert>
      <Alert type="danger" state={!!warning} text={warning} />
    </>
  );
};

const createFiles = ({
  schema,
  api,
  dispatcher,
  error,
}: CreateFiles): React.ReactElement<any> => {
  return schema?.visible === undefined || schema?.visible ? (
    <Field {...schema}>
      <FilesControl
        schema={schema as FilesSchema}
        api={api}
        dispatcher={dispatcher}
        error={error}
      />
    </Field>
  ) : null;
};

export { createFiles, FilesControl };
